import EntityService from "@/services/entityService";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const normStr = (v) => String(v || "").trim();

// Extrai texto de campos que podem ser objetos/arrays (relations)
const extrairTexto = (v) => {
  if (!v) return "";
  if (Array.isArray(v)) return v.length ? extrairTexto(v[0]) : "";
  if (typeof v === "object") {
    return normStr(v.name || v.nome || v.fullName || v.displayName || v.descricao || v.description || v.code || v.codigo || "");
  }
  const s = normStr(v);
  // Se parece um UUID, retorna vazio
  if (/^[0-9a-f]{8,}-/i.test(s) || /^[0-9a-f]{24}$/i.test(s)) return "";
  return s;
};

const formatarData = (valor) => {
  if (!valor) return "";
  try {
    const d = new Date(valor);
    if (isNaN(d.getTime())) return normStr(valor).slice(0, 10);
    return d.toLocaleDateString("pt-BR", { timeZone: "UTC" });
  } catch {
    return normStr(valor).slice(0, 10);
  }
};

const formatarMoeda = (valor) => {
  const n = Number(valor || 0);
  return n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const dataHoje = () => new Date().toLocaleDateString("pt-BR");

// ─────────────────────────────────────────────────────────────────────────────
// Logo
// ─────────────────────────────────────────────────────────────────────────────

let _logoCache = null;

const carregarLogo = async () => {
  if (_logoCache) return _logoCache;
  try {
    const response = await fetch("/img/logo-delta-ducon.png");
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => { _logoCache = reader.result; resolve(_logoCache); };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// OS enrichment – busca OS (OrdemDeServico) vinculadas ao código do JOB
// ─────────────────────────────────────────────────────────────────────────────

const extrairLista = (response) => {
  const lista = response?.data?.data?.data;
  if (Array.isArray(lista)) return lista;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const buscarTodasOS = async () => {
  const candidatos = ["OrdemDeServico", "OrderService", "OS"];
  for (const schema of candidatos) {
    try {
      const svc = new EntityService(schema);
      const resp = await svc.getAll(1, 5000, "", "", null, null, null, "codigo");
      const lista = extrairLista(resp);
      if (Array.isArray(lista) && lista.length) return lista;
    } catch { /* tenta próximo */ }
  }
  return [];
};

const normalizarOS = (os = {}) => {
  const raw = os?._currentData || os;

  const obterJobCode = () => {
    for (const campo of ["jobCode", "jobCodigo", "job", "project", "projectCode"]) {
      const v = raw[campo];
      if (!v) continue;
      if (typeof v === "string") return v;
      if (Array.isArray(v) && v[0]) {
        const item = v[0];
        return typeof item === "string" ? item : (item?.code || item?.codigo || "");
      }
      if (typeof v === "object") return v?.code || v?.codigo || "";
    }
    return "";
  };

  const d = (...campos) => {
    for (const c of campos) {
      const v = raw[c];
      if (v && typeof v === "string" && v.length >= 8) return v;
    }
    return null;
  };

  return {
    codigo: normStr(raw?.codigo || raw?.code || raw?.osCodigo || "-"),
    descricao: normStr(raw?.descricao || raw?.description || "-"),
    tipo: extrairTexto(raw?.tipo || raw?.tipoOS || raw?.tipoEquipamento || raw?.type || ""),
    jobCode: obterJobCode(),
    dataFabricacaoPrevista: d("dataFabricacaoPrevista", "fabricacaoPrevista", "dateFabPrev"),
    dataFabricacaoEfetiva: d("dataFabricacaoEfetiva", "fabricacaoEfetiva", "dateFabEfetiva"),
    dataMontagemPrevista: d("dataMontagemPrevista", "montagemPrevista", "dateMontagemPrev", "dateContratualMontagem"),
    dataMontagemEfetiva: d("dataMontagemEfetiva", "montagemEfetiva", "dateMontagemEfetiva", "dateEfetivaMontagem")
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// Agrupamento
// ─────────────────────────────────────────────────────────────────────────────

const agruparPorCoordenador = (listaJobs, osPorJob) => {
  const grupos = {};
  for (const job of listaJobs) {
    const coord = normStr(job.vendedorCoordenador || "-");
    if (!grupos[coord]) grupos[coord] = [];
    const osLinhas = osPorJob[normStr(job.code).toUpperCase()] || [];
    grupos[coord].push({ job, osLinhas });
  }
  return grupos;
};

const siglaContratosProjetos = (job = {}) => {
  const siglaPronta = extrairTexto(job?.contratosProjetosSigla || "");
  if (siglaPronta && siglaPronta.includes("/")) return siglaPronta;

  const extrairIniciais = (nome) =>
    normStr(nome)
      .split(/\s+/)
      .filter(Boolean)
      .map((parte) => parte.charAt(0).toUpperCase())
      .join("");

  const gestor = extrairTexto(job?.gestorContratosNome || job?.gestorDeContratosNome || job?.gestorDeContratos || "");
  const coordenador = extrairTexto(job?.coordenadorNome || job?.vendedorCoordenador || "");
  const siglaGestor = extrairIniciais(gestor);
  const siglaCoordenador = extrairIniciais(coordenador);

  if (siglaGestor && siglaCoordenador) return `${siglaGestor}/${siglaCoordenador}`;
  if (siglaGestor) return `${siglaGestor}/-`;
  if (siglaCoordenador) return `-/${siglaCoordenador}`;
  return siglaPronta || "";
};

// ─────────────────────────────────────────────────────────────────────────────
// Células e estilos compartilhados
// ─────────────────────────────────────────────────────────────────────────────

const FONT_DATA = 6;
const FONT_HEADER = 6;
const N = 22; // total de colunas

const cel = (text, opts = {}) => ({
  text: text == null ? "" : String(text),
  fontSize: opts.fontSize || FONT_DATA,
  bold: opts.bold || false,
  alignment: opts.alignment || "center",
  fillColor: opts.fillColor || null,
  color: opts.color || "#000000",
  rowSpan: opts.rowSpan || null,
  colSpan: opts.colSpan || null,
  border: opts.border || [true, true, true, true],
  margin: [1, 1, 1, 1],
  ...opts
});

const celHeader = (text, opts = {}) =>
  cel(text, { bold: true, fillColor: "#D0D8E4", fontSize: FONT_HEADER, ...opts });

const celData = (text, opts = {}) =>
  cel(text, { fontSize: FONT_DATA, ...opts });

const celCoord = (nome) =>
  cel(`Coordenador: ${nome}`, {
    colSpan: N,
    bold: true,
    fontSize: 7,
    fillColor: "#E8E8E8",
    alignment: "left"
  });

const celVazio = () => celData("");

// ─────────────────────────────────────────────────────────────────────────────
// Linhas de cabeçalho da tabela (2 linhas)
// ─────────────────────────────────────────────────────────────────────────────

const montarCabecalho = () => {
  // Linha 1 (22 colunas)
  const linha1 = [
    celHeader("Encom.\ndx JOB",        { rowSpan: 2 }),
    celHeader("Cliente",                { rowSpan: 2, alignment: "left" }),
    celHeader("Contr./\nProjetos",      { rowSpan: 2 }),
    celHeader("Equipamento / OS's",     { rowSpan: 2, alignment: "left" }),
    celHeader("Data do\nPedido",        { rowSpan: 2 }),
    celHeader("Envio Des.\np/Aprov",    { colSpan: 2 }), celVazio(),
    celHeader("Aprovação\nDas",         { colSpan: 2 }), celVazio(),
    celHeader("Detalha-\nmento",        { colSpan: 2 }), celVazio(),
    celHeader("Fabricação",             { colSpan: 2 }), celVazio(),
    celHeader("Montagem",               { colSpan: 2 }), celVazio(),
    celHeader("Entr.Final\nContr.",     { colSpan: 2 }), celVazio(),
    celHeader("Condições de\nPagamento",{ rowSpan: 2, alignment: "left" }),
    celHeader("Mul",                    { rowSpan: 2 }),
    celHeader("Tra",                    { rowSpan: 2 }),
    celHeader("Valores",                { colSpan: 2 }), celVazio()
  ];

  // Linha 2 – subheaders de Prev/Efet e Total/Saldo (apenas as colunas de grupo)
  // Posicionamos "fillers" ({}) onde rowSpan os ocupa, e subheaders onde cabem
  const linha2 = [
    {}, {}, {}, {}, {},                          // rowSpan cobertos (5 colunas)
    celHeader("Prev."), celHeader("Efet."),       // Envio
    celHeader("Prev."), celHeader("Efet."),       // Aprovação
    celHeader("Prev."), celHeader("Efet."),       // Detalhamento
    celHeader("Prev."), celHeader("Efet."),       // Fabricação
    celHeader("Prev."), celHeader("Efet."),       // Montagem
    celHeader("Prev."), celHeader("Efet."),       // EntregaFinal
    {},                                           // CondPag rowSpan
    {}, {},                                       // Mul/Tra rowSpan
    celHeader("Total"), celHeader("Saldo")        // Valores
  ];

  return [linha1, linha2];
};

// ─────────────────────────────────────────────────────────────────────────────
// Linha OS – contém dados da OS + dados do JOB complementares
// ─────────────────────────────────────────────────────────────────────────────

const linhaOS = (os, job) => {
  const aln = (txt, opts) => celData(txt, { alignment: "left", ...opts });
  const dc = (txt, opts) => celData(txt, opts);

  const bool = (v) => v ? "S" : "N";
  const sem = (flag, prevista, efetiva) => {
    if (flag) return ["N/T", "N/T"];
    return [formatarData(prevista) || "–", formatarData(efetiva) || ""];
  };

  // Datas do JOB (fases compartilhadas)
  const [envPrev, envEfet] = sem(job.naoTemEnvioAprovacao, job.dataEnvioAprovacaoPrevista, job.dataEnvioAprovacaoEfetiva);
  const [apvPrev, apvEfet] = sem(job.naoTemAprovacao, job.dataAprovacaoPrevista, job.dataAprovacaoEfetiva);
  const [detPrev, detEfet] = sem(job.naoTemDetalhamento, job.dataDetalhamentoPrevista, job.dataDetalhamentoEfetiva);
  const entPrev = formatarData(job.dataEntregaFinalPrevista) || "–";
  const entEfet = formatarData(job.dataEntregaFinalEfetiva) || "";

  // Datas de Fabricação e Montagem vêm da OS
  const fabPrev = formatarData(os.dataFabricacaoPrevista) || "–";
  const fabEfet = formatarData(os.dataFabricacaoEfetiva) || "";
  const mntPrev = formatarData(os.dataMontagemPrevista) || "–";
  const mntEfet = formatarData(os.dataMontagemEfetiva) || "";

  const descricao = os.codigo && os.descricao && os.descricao !== "-"
    ? `${os.codigo} - ${os.descricao}`
    : (os.codigo || os.descricao || "");

  return [
    aln(job.code, { bold: true }),
    aln(job.clienteNome),
    dc(siglaContratosProjetos(job)),
    aln(descricao),
    dc(formatarData(job.dataAbertura)),
    dc(envPrev), dc(envEfet),
    dc(apvPrev), dc(apvEfet),
    dc(detPrev), dc(detEfet),
    dc(fabPrev), dc(fabEfet),
    dc(mntPrev), dc(mntEfet),
    dc(entPrev), dc(entEfet),
    aln(job.condicaoDePagamento),
    dc(bool(job.multa)),
    dc(bool(job.transporte)),
    dc(formatarMoeda(job.valorJob), { alignment: "right" }),
    dc(formatarMoeda(job.saldoAReceber), { alignment: "right" })
  ];
};

// Linha JOB sem OS vinculada
const linhaJobSemOS = (job) => {
  const aln = (txt, opts) => celData(txt, { alignment: "left", ...opts });
  const dc = (txt, opts) => celData(txt, opts);

  const bool = (v) => v ? "S" : "N";
  const sem = (flag, prevista, efetiva) => {
    if (flag) return ["N/T", "N/T"];
    return [formatarData(prevista) || "–", formatarData(efetiva) || ""];
  };

  const [envPrev, envEfet] = sem(job.naoTemEnvioAprovacao, job.dataEnvioAprovacaoPrevista, job.dataEnvioAprovacaoEfetiva);
  const [apvPrev, apvEfet] = sem(job.naoTemAprovacao, job.dataAprovacaoPrevista, job.dataAprovacaoEfetiva);
  const [detPrev, detEfet] = sem(job.naoTemDetalhamento, job.dataDetalhamentoPrevista, job.dataDetalhamentoEfetiva);
  const [fabPrev, fabEfet] = sem(job.naoTemFabricacao, job.dataFabricacaoPrevista, job.dataFabricacaoEfetiva);
  const [mntPrev, mntEfet] = sem(job.naoTemMontagem, job.dataMontagemPrevista, job.dataMontagemEfetiva);
  const entPrev = formatarData(job.dataEntregaFinalPrevista) || "–";
  const entEfet = formatarData(job.dataEntregaFinalEfetiva) || "";

  return [
    aln(job.code, { bold: true }),
    aln(job.clienteNome),
    dc(siglaContratosProjetos(job)),
    dc(""),
    dc(formatarData(job.dataAbertura)),
    dc(envPrev), dc(envEfet),
    dc(apvPrev), dc(apvEfet),
    dc(detPrev), dc(detEfet),
    dc(fabPrev), dc(fabEfet),
    dc(mntPrev), dc(mntEfet),
    dc(entPrev), dc(entEfet),
    aln(job.condicaoDePagamento),
    dc(bool(job.multa)),
    dc(bool(job.transporte)),
    dc(formatarMoeda(job.valorJob), { alignment: "right" }),
    dc(formatarMoeda(job.saldoAReceber), { alignment: "right" })
  ];
};

// ─────────────────────────────────────────────────────────────────────────────
// Larguras das colunas
// ─────────────────────────────────────────────────────────────────────────────

const WIDTHS = [
  40,   // 1  Encom/JOB
  98,   // 2  Cliente
  26,   // 3  Contratos
  215,  // 4  Equipamento
  40,   // 5  Data Pedido
  33, 33, // 6-7 Envio
  33, 33, // 8-9 Aprovação
  33, 33, // 10-11 Detalhamento
  33, 33, // 12-13 Fabricação
  33, 33, // 14-15 Montagem
  33, 33, // 16-17 Entrega Final
  108,  // 18 CondPag
  14,   // 19 Mul
  14,   // 20 Tra
  52,   // 21 Total
  52    // 22 Saldo
]; // Total ≈ 1065pt (A3 landscape usável ~1175pt)

// ─────────────────────────────────────────────────────────────────────────────
// Monta corpo da tabela
// ─────────────────────────────────────────────────────────────────────────────

const montarCorpo = (grupos) => {
  const linhas = [];

  for (const [, jobs] of Object.entries(grupos)) {
    for (const { job, osLinhas } of jobs) {
      const lc = [celCoord(job.coordenadorNome || job.vendedorCoordenador || "-")];
      for (let i = 1; i < N; i++) lc.push({});
      linhas.push(lc);

      if (osLinhas.length > 0) {
        // Uma linha por OS, com dados do JOB complementando
        for (const os of osLinhas) {
          linhas.push(linhaOS(os, job));
        }
      } else {
        // Sem OS vinculada: exibe linha do JOB
        linhas.push(linhaJobSemOS(job));
      }
    }
  }

  return linhas;
};

// ─────────────────────────────────────────────────────────────────────────────
// Constrói definição pdfmake
// ─────────────────────────────────────────────────────────────────────────────

const criarDefinicaoPDF = (grupos, logo) => {
  const cabecalho = montarCabecalho();
  const corpo = montarCorpo(grupos);

  const logoContent = logo
    ? { image: logo, width: 60, margin: [0, 0, 10, 0] }
    : { text: "" };

  return {
    pageSize: "A3",
    pageOrientation: "landscape",
    pageMargins: [5, 42, 5, 18],

    header: {
      stack: [
        {
          columns: [
            logoContent,
            {
              text: `Cronograma de Coordenação de Contratos - Situação em ${dataHoje()}`,
              style: "titulo",
              alignment: "center",
              margin: [0, 6, 0, 0]
            }
          ],
          margin: [8, 5, 8, 0]
        }
      ]
    },

    footer: (currentPage, pageCount) => ({
      columns: [
        { text: "R_Cronograma.Ret", fontSize: 6, margin: [15, 0, 0, 0] },
        { text: `Emissão: ${dataHoje()} - Pág.: ${currentPage}`, fontSize: 6, alignment: "right", margin: [0, 0, 8, 0] }
      ]
    }),

    content: [
      {
        table: {
          headerRows: 2,
          widths: WIDTHS,
          body: [...cabecalho, ...corpo]
        },
        layout: {
          hLineWidth: () => 0.25,
          vLineWidth: () => 0.25,
          hLineColor: () => "#888888",
          vLineColor: () => "#888888",
          paddingTop: () => 1,
          paddingBottom: () => 1,
          paddingLeft: () => 1,
          paddingRight: () => 1
        }
      }
    ],

    styles: {
      titulo: { fontSize: 10, bold: true }
    },

    defaultStyle: {
      font: "Roboto",
      fontSize: FONT_DATA
    }
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// API pública
// ─────────────────────────────────────────────────────────────────────────────

export default {
  /**
   * Gera o PDF do Cronograma de JOB.
   * @param {Array} listaJobs - Array normalizado de relatorioJobService
   */
  async gerarCronograma(listaJobs = []) {
    if (!listaJobs.length) return;

    let todasOS = [];
    try {
      todasOS = await buscarTodasOS();
    } catch (e) {
      console.warn("[UtilCronogramaJobPDF] Não foi possível carregar OS:", e);
    }

    // Normaliza e mapeia OS por código de JOB (uppercase)
    const osPorJob = {};
    for (const os of todasOS) {
      const norm = normalizarOS(os);
      const chave = normStr(norm.jobCode).toUpperCase();
      if (!chave) continue;
      if (!osPorJob[chave]) osPorJob[chave] = [];
      osPorJob[chave].push(norm);
    }

    const grupos = agruparPorCoordenador(listaJobs, osPorJob);

    const logo = await carregarLogo();
    const docDef = criarDefinicaoPDF(grupos, logo);

    const pdfMake = (await import("pdfmake/build/pdfmake")).default;
    const pdfFonts = (await import("pdfmake/build/vfs_fonts")).default;
    pdfMake.vfs = pdfFonts.pdfMake.vfs;

    const hoje = new Date().toISOString().slice(0, 10);
    const nomeArquivo = `Cronograma_JOB_${hoje}.pdf`;

    pdfMake.createPdf(docDef).download(nomeArquivo);
  },

  /**
   * Gera dados estruturados para exportação Excel (array de arrays).
   * @param {Array} listaJobs
   * @returns {Array} rows prontas para XLSX.utils.aoa_to_sheet
   */
  async gerarLinhasExcel(listaJobs = []) {
    let todasOS = [];
    try {
      todasOS = await buscarTodasOS();
    } catch { /* silencioso */ }

    const osPorJob = {};
    for (const os of todasOS) {
      const norm = normalizarOS(os);
      const chave = normStr(norm.jobCode).toUpperCase();
      if (!chave) continue;
      if (!osPorJob[chave]) osPorJob[chave] = [];
      osPorJob[chave].push(norm);
    }

    const grupos = agruparPorCoordenador(listaJobs, osPorJob);

    const CAB = [
      "Encom./JOB", "Cliente", "Contratos/Projetos", "Equipamento / OS's", "Data do Pedido",
      "Envio Prev.", "Envio Efet.",
      "Aprov. Prev.", "Aprov. Efet.",
      "Detal. Prev.", "Detal. Efet.",
      "Fabr. Prev.", "Fabr. Efet.",
      "Mont. Prev.", "Mont. Efet.",
      "Entr.Final Prev.", "Entr.Final Efet.",
      "Condições de Pagamento", "Mul", "Tra", "Total", "Saldo"
    ];

    const rows = [CAB];
    const bool = (v) => v ? "S" : "N";
    const sem = (flag, prev, efet) => flag ? ["N/T", "N/T"] : [formatarData(prev) || "–", formatarData(efet) || ""];

    for (const [, jobs] of Object.entries(grupos)) {
      for (const { job, osLinhas } of jobs) {
        rows.push([`Coordenador: ${job.coordenadorNome || job.vendedorCoordenador || "-"}`]);

        const [ep, ee] = sem(job.naoTemEnvioAprovacao, job.dataEnvioAprovacaoPrevista, job.dataEnvioAprovacaoEfetiva);
        const [ap, ae] = sem(job.naoTemAprovacao, job.dataAprovacaoPrevista, job.dataAprovacaoEfetiva);
        const [dp, de] = sem(job.naoTemDetalhamento, job.dataDetalhamentoPrevista, job.dataDetalhamentoEfetiva);
        const entPrev = formatarData(job.dataEntregaFinalPrevista) || "–";
        const entEfet = formatarData(job.dataEntregaFinalEfetiva) || "";

        if (osLinhas.length > 0) {
          // Uma linha por OS, com dados do JOB complementando
          for (const os of osLinhas) {
            const descricao = os.codigo && os.descricao && os.descricao !== "-"
              ? `${os.codigo} - ${os.descricao}` : (os.codigo || os.descricao || "");
            rows.push([
              job.code, job.clienteNome, siglaContratosProjetos(job), descricao,
              formatarData(job.dataAbertura),
              ep, ee, ap, ae, dp, de,
              formatarData(os.dataFabricacaoPrevista) || "–",
              formatarData(os.dataFabricacaoEfetiva) || "",
              formatarData(os.dataMontagemPrevista) || "–",
              formatarData(os.dataMontagemEfetiva) || "",
              entPrev, entEfet,
              job.condicaoDePagamento, bool(job.multa), bool(job.transporte),
              job.valorJob, job.saldoAReceber
            ]);
          }
        } else {
          // JOB sem OS vinculada
          const [fp, fe] = sem(job.naoTemFabricacao, job.dataFabricacaoPrevista, job.dataFabricacaoEfetiva);
          const [mp, me] = sem(job.naoTemMontagem, job.dataMontagemPrevista, job.dataMontagemEfetiva);
          rows.push([
            job.code, job.clienteNome, siglaContratosProjetos(job), "",
            formatarData(job.dataAbertura),
            ep, ee, ap, ae, dp, de, fp, fe, mp, me,
            entPrev, entEfet,
            job.condicaoDePagamento, bool(job.multa), bool(job.transporte),
            job.valorJob, job.saldoAReceber
          ]);
        }
      }
    }

    return rows;
  }
};
